/**
 * CR321: EligibilityBoxBlock
 * CR413: Structured eligibility requirements V1 (feature-flagged)
 * 
 * Renders computed eligibility information on the back surface.
 * 
 * When ff_eligibility_requirements_v1 is ON:
 *   - Uses stored eligibility_requirements if valid v1
 *   - Falls back to deriveEligibilityRequirementsV1()
 *   - Renders as ordered bullet list (Where / Buy / When / Get / Also)
 * 
 * When flag is OFF:
 *   - Uses buildEligibilityComputed (legacy behavior, unchanged)
 */

import React from "react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { buildEligibilityComputed, type EligibilityComputed } from "@/lib/deal-cards/eligibilityComputed";
import {
  isValidEligibilityRequirementsV1,
  deriveEligibilityRequirementsV1,
  eligibilityToBullets,
} from "@/lib/deals/eligibilityRequirements";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FLAGS } from "@/lib/featureFlags";
import { MapPin, Calendar, ShoppingBag, AlertCircle, Info, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

export const EligibilityBoxBlock: React.FC<DealCardBlockProps<Deal>> = ({
  deal,
  block,
  context,
}) => {
  if (!block.enabled) return null;

  const useStructured = useFeatureFlag(FLAGS.ELIGIBILITY_REQUIREMENTS_V1);

  if (useStructured) {
    return (
      <StructuredEligibility deal={deal} block={block} context={context} />
    );
  }

  return <LegacyEligibility deal={deal} block={block} context={context} />;
};

// =============================================================================
// CR413: STRUCTURED BULLET RENDERING
// =============================================================================

const StructuredEligibility: React.FC<DealCardBlockProps<Deal>> = ({ deal, block, context }) => {
  // Resolve requirements: stored → fallback derivation
  const extDeal = deal as Deal & { eligibility_requirements?: unknown };
  const stored = extDeal.eligibility_requirements;

  const requirements = isValidEligibilityRequirementsV1(stored)
    ? stored
    : deriveEligibilityRequirementsV1(deal);

  const bullets = eligibilityToBullets(requirements);

  if (bullets.length === 0) return null;

  return (
    <div
      className="space-y-3"
      data-block="eligibility_box"
      data-block-id={block.id}
    >
      <h4 className="text-sm font-semibold text-foreground">
        {context.labels?.eligibility_box_title || context.labels?.eligibility_title || "Eligibility"}
      </h4>
      <ul className="space-y-1.5 text-sm">
        {bullets.map((bullet, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
            <span className="text-foreground">{bullet}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// =============================================================================
// LEGACY ELIGIBILITY (flag OFF — unchanged from CR321)
// =============================================================================

const LegacyEligibility: React.FC<DealCardBlockProps<Deal>> = ({ deal, block, context }) => {
  const eligibility = buildEligibilityComputed(deal);

  if (!eligibility.hasContent) return null;

  const isAdmin = context.isAdmin;

  return (
    <div
      className="space-y-3"
      data-block="eligibility_box"
      data-block-id={block.id}
    >
      <h4 className="text-sm font-semibold text-foreground">
        {context.labels?.eligibility_box_title || context.labels?.eligibility_title || "Eligibility"}
      </h4>

      <div className="space-y-2.5">
        {eligibility.what && (
          <EligibilityRow
            icon={<ShoppingBag className="h-3.5 w-3.5" />}
            label="Qualifying Purchase"
            value={eligibility.what}
          />
        )}
        {eligibility.where && (
          <EligibilityRow
            icon={<MapPin className="h-3.5 w-3.5" />}
            label="Where"
            value={eligibility.where}
          />
        )}
        {eligibility.when && (
          <EligibilityRow
            icon={<Calendar className="h-3.5 w-3.5" />}
            label="When"
            value={eligibility.when}
          />
        )}
        {eligibility.limits.length > 0 && (
          <EligibilityRow
            icon={<AlertCircle className="h-3.5 w-3.5" />}
            label="Limits"
            value={<BulletList items={eligibility.limits} />}
          />
        )}
        {eligibility.exclusions.length > 0 && (
          <EligibilityRow
            icon={<AlertCircle className="h-3.5 w-3.5" />}
            label="Exclusions"
            value={<BulletList items={eligibility.exclusions} />}
            variant="warning"
          />
        )}
        {eligibility.notes && (
          <EligibilityRow
            icon={<Info className="h-3.5 w-3.5" />}
            label="Note"
            value={eligibility.notes}
          />
        )}
        {isAdmin && eligibility.proof.length > 0 && (
          <div className="pt-2 border-t border-border/50">
            <EligibilityRow
              icon={<FileText className="h-3.5 w-3.5" />}
              label="Evidence (Admin)"
              value={<BulletList items={eligibility.proof} />}
              variant="admin"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// SUB-COMPONENTS (shared by legacy path)
// =============================================================================

interface EligibilityRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  variant?: "default" | "warning" | "admin";
}

const EligibilityRow: React.FC<EligibilityRowProps> = ({
  icon,
  label,
  value,
  variant = "default",
}) => (
  <div className="flex items-start gap-2 text-sm">
    <span
      className={cn(
        "mt-0.5 flex-shrink-0",
        variant === "warning" && "text-destructive",
        variant === "admin" && "text-accent-foreground opacity-70",
        variant === "default" && "text-muted-foreground"
      )}
    >
      {icon}
    </span>
    <div className="flex-1 min-w-0">
      <span className="font-medium text-muted-foreground">{label}: </span>
      <span
        className={cn(
          "text-foreground",
          variant === "admin" && "text-muted-foreground italic"
        )}
      >
        {value}
      </span>
    </div>
  </div>
);

interface BulletListProps {
  items: string[];
}

const BulletList: React.FC<BulletListProps> = ({ items }) => {
  if (items.length === 1) return <span>{items[0]}</span>;
  return (
    <ul className="list-disc list-inside space-y-0.5 mt-0.5">
      {items.map((item, idx) => (
        <li key={idx} className="text-sm">{item}</li>
      ))}
    </ul>
  );
};

export default EligibilityBoxBlock;
